import React, { Component } from 'react';
import { any, identity, range } from 'ramda';
import { Button } from 'js/components/common/Button';
import { withTranslations } from 'js/components/hocs/withTranslations';
import { LocalPopup } from 'js/components/common/Popup';
import Timer from './Timer';
import Keyboard from './Keyboard';
import { UNLIMITED } from './constants';
import { getInitialIndex, msToSegments, periodsToMs, segmentsToMs, segmentsToPeriods } from './helpers';
import style from './index.scss';

// [m0, m1, h0, h1, d0, d1]
export const SEGMENTS = [0, 0, 0, 0, 0, 0];

export class Picker extends Component {
    constructor(props) {
        super(props);

        const { value, useDays, useHours } = props;
        const isUnlimited = value === UNLIMITED;
        const initialIndex = getInitialIndex({ useDays, useHours });

        const segments = isUnlimited
            ? [...SEGMENTS]
            : msToSegments(value, initialIndex);

        this.state = {
            segments,
            isUnlimited,
            initialIndex,
            activeIndex: initialIndex,
            isError: false,
            isErrorPeriods: [false, false, false],
            triggerShake: false
        };
    }

    componentDidMount() {
        window.addEventListener('keydown', this.onKeyDown);
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.onKeyDown);
    }

    setUnlimited = () => {
        const { initialIndex: activeIndex } = this.state;
        const segments = [...SEGMENTS];

        this.setState({
            isUnlimited: !this.state.isUnlimited,
            segments,
            activeIndex: this.state.isUnlimited ? activeIndex : 1,
            isError: false,
            isErrorPeriods: [false, false, false]
        });
    };

    getActivePeriodInitialIndex = () => {
        const { activeIndex } = this.state;

        return activeIndex % 2 === 0
            ? activeIndex + 1
            : activeIndex;
    };

    validateInput = (segments) => {
        const { maxDays, maxHours, maxMinutes, useDays, useHours, useMinutes } = this.props;
        const validationMap = [
            useDays ? maxDays : Infinity,
            useHours ? maxHours : Infinity,
            useMinutes ? maxMinutes : Infinity
        ];
        const periods = segmentsToPeriods(segments);
        const firstPeriodIndex = useDays
            ? 0
            : useHours
                ? 1
                : 2;
        validationMap[firstPeriodIndex] += 1;

        const maxValuePeriods = [0, 0, 0];
        maxValuePeriods[firstPeriodIndex] = validationMap[firstPeriodIndex] - 1;

        const maxValue = periodsToMs(maxValuePeriods);
        const currentValue = segmentsToMs(segments);

        const isErrorPeriods = periods.map((period, i) => period >= validationMap[i]);
        const isError = isErrorPeriods.includes(true) ? false : currentValue > maxValue;

        return { isError, isErrorPeriods };
    };

    updateSegments = (value, reset = false) => {
        const numValue = Number(value);
        const { activeIndex, segments } = this.state;
        const index = reset
            ? this.getActivePeriodInitialIndex()
            : activeIndex;
        const newSegments = [...segments];

        if (index % 2 === 0) {
            newSegments[index + 1] = newSegments[index];
            newSegments[index] = numValue;
        } else { // when reset index will always be odd
            newSegments[index] = 0;
            newSegments[index - 1] = numValue;
        }

        return newSegments;
    };

    advanceActiveIndex = (isError, isErrorPeriods, reset = false) => {
        const { activeIndex } = this.state;

        // advance only on normal valid input. Otherwise - reset
        return (
            reset
            || activeIndex === 0
            || activeIndex % 2 === 0 && (isError || isErrorPeriods.includes(true))
                ? this.getActivePeriodInitialIndex()
                : activeIndex - 1
        );
    };

    updateData = (value, reset = false) => {
        const segments = this.updateSegments(value, reset);
        const { isError, isErrorPeriods } = this.validateInput(segments);
        const nextActiveIndex = this.advanceActiveIndex(isError, isErrorPeriods, reset);

        return this.setState({
            segments,
            activeIndex: nextActiveIndex,
            isError,
            isErrorPeriods
        });
    };

    onSubmit = () => {
        const { isUnlimited, segments, isError, isErrorPeriods, triggerShake } = this.state;

        if (isError || isErrorPeriods.includes(true)) {
            this.setState({
                triggerShake: !triggerShake,
                activeIndex: this.getActivePeriodInitialIndex()
            });

            return;
        }

        const { onSubmit } = this.props;
        const value = isUnlimited
            ? UNLIMITED
            : segmentsToMs(segments);

        onSubmit(value);
    };

    onCancel = () => {
        this.updateData(0, true);
    };

    onSelect = (activeIndex) => {
        if (activeIndex !== this.state.activeIndex) {
            this.setState({ activeIndex });
        }
    };

    onInput = (value) => {
        this.updateData(value);
    };

    onUnlimitedClick = () => {
        this.setUnlimited();
    };

    onKeyDown = (e) => {
        const { key, altKey, ctrlKey, shiftKey, metaKey } = e;
        const activeIndex = this.getActivePeriodInitialIndex();

        if (any(identity, [altKey, ctrlKey, shiftKey, metaKey])) {
            return;
        }

        const numbers = range(0, 10).map(String);

        if (numbers.includes(key)) {
            this.updateData(key);
        } else if (key === 'Backspace') {
            this.onCancel();
        } else if (key === 'Tab') {
            const { initialIndex } = this.state;
            const currentIndex = activeIndex === -1 ? initialIndex : activeIndex - 2;
            let unlimitedIndex;

            if (activeIndex === -1) {
                unlimitedIndex = 1;
            } else if (activeIndex > 1) {
                unlimitedIndex = -1;
            } else {
                unlimitedIndex = activeIndex - 2;
            }

            this.onSelect(this.state.isUnlimited ? unlimitedIndex : currentIndex);
        } else if (key === 'Enter') {
            activeIndex === -1 ? this.onUnlimitedClick() : this.onSubmit();
        } else if (key === 'Escape') {
            this.props.onClose();
        } else {
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
    };

    render() {
        const {
            onClose,
            title,
            useDays,
            useHours,
            useMinutes,
            allowUnlimited,
            t
        } = this.props;
        const { segments, activeIndex, isUnlimited, isError, isErrorPeriods, triggerShake } = this.state;
        const { onSelect, onCancel, onInput, onSubmit } = this;

        return (
            <LocalPopup
                onClose={onClose}
                className={style.picker}
                classNames={{ close: style.close, header: style.header }}
                iconType="close"
            >
                <h2 className={style.title}>{t(title)}</h2>
                <div className={style.container}>
                    <div className={style.column}>
                        <Timer {...{
                            segments,
                            useDays,
                            useHours,
                            useMinutes,
                            isUnlimited,
                            onSelect,
                            activeIndex,
                            isError,
                            isErrorPeriods,
                            triggerShake
                        }} />
                        {isUnlimited && (
                            <Button
                                onClick={this.onUnlimitedClick}
                                className={style.unlimitedBtn}
                                status={activeIndex === -1 ? 'hover' : null}
                                view="secondary">
                                {t('playtech.mobile.system.dp.unlimited.period.unset')}
                            </Button>
                        )}
                        {!isUnlimited && allowUnlimited && (
                            <Button
                                onClick={this.onUnlimitedClick}
                                className={style.unlimitedBtn}
                                status={activeIndex === -1 ? 'hover' : null}
                                view="secondary">
                                {t('playtech.mobile.system.dp.unlimited.period.set')}
                            </Button>
                        )}
                    </div>
                    <Keyboard {...{
                        t,
                        isUnlimited,
                        onInput,
                        onCancel,
                        onSubmit
                    }} />
                </div>
            </LocalPopup>
        );
    }
}

export default withTranslations(Picker);
